/**
 * SoloStatsHeader — the "chase your high score" stat strip above the solo race
 * track. Three cells, each showing THIS session's live value + your personal best
 * (for the current time control): Streak · Points · Solves. When a live value
 * crosses its best, the cell count-ups to the new best and does one scale-pop
 * (reusing the AnimatedMCI pattern from RoyalePuzzleGridPanel).
 *
 * Icons only (MaterialCommunityIcons) — no emojis.
 *
 * `best*` come from useSoloPersonalBest (per time control). Before the first game
 * the best is 0 with hasPb=false; the cell just shows a dash for "best".
 */

import React, {useEffect, useRef} from 'react';
import {Animated, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const BROWN = CCColors.Primary?.Brown ?? '#4D3B22';
const GREY_TEXT = '#8A8677';
const TEAL = '#3DAB9E';
const GOLD = '#F0B429';
const CARD_BG = CCColors.White ?? '#FFFFFF';
const BORDER = 'rgba(38, 35, 34, 0.10)';

type StatKey = 'streak' | 'points' | 'solves';

interface StatSpec {
  key: StatKey;
  label: string;
  icon: string; // MaterialCommunityIcons glyph
}

const STATS: StatSpec[] = [
  {key: 'streak', label: 'Streak', icon: 'lightning-bolt'},
  {key: 'points', label: 'Points', icon: 'star-four-points'},
  {key: 'solves', label: 'Solves', icon: 'check-decagram'},
];

interface Props {
  /** Live this-session values. */
  current: {streak: number; points: number; solves: number};
  /** Personal best for the current time control (0s before first game). */
  best: {streak: number; points: number; solves: number};
  /** False before the player's first-ever solo game at this time control. */
  hasPb: boolean;
}

export default function SoloStatsHeader({current, best, hasPb}: Props) {
  return (
    <View style={styles.row}>
      {STATS.map(s => (
        <StatCell
          key={s.key}
          spec={s}
          current={current[s.key]}
          best={best[s.key]}
          hasPb={hasPb}
        />
      ))}
    </View>
  );
}

function StatCell({
  spec,
  current,
  best,
  hasPb,
}: {
  spec: StatSpec;
  current: number;
  best: number;
  hasPb: boolean;
}) {
  // "Beating your best" = live value STRICTLY exceeds the pre-game baseline best
  // (a genuine new record — a tie is not a new best), or you're climbing from a
  // 0 baseline on your first-ever game. `best` is the baseline PB frozen at
  // session start by the parent, so `beating` stays true after the game ends
  // (the highlight persists on the record you just set).
  const beating = hasPb ? current > best : current > 0;
  // The record shown: your new value once you've beaten the baseline, else it.
  const displayBest = Math.max(best, current);

  // Scale-pop once each time `beating` flips true (a fresh crossing) and on each
  // subsequent increment while beating. A little springier + bigger on the
  // beating cell so the "new best" moment reads clearly (item 1).
  const pop = useRef(new Animated.Value(0)).current;
  const prevCurrent = useRef(current);
  useEffect(() => {
    const crossed = current > prevCurrent.current && beating;
    prevCurrent.current = current;
    if (!crossed) {
      return;
    }
    pop.setValue(0);
    Animated.sequence([
      Animated.spring(pop, {
        toValue: 1,
        speed: 20,
        bounciness: 14,
        useNativeDriver: true,
      }),
      Animated.timing(pop, {toValue: 0, duration: 180, useNativeDriver: true}),
    ]).start();
  }, [current, beating, pop]);

  // Pop only the NUMBER (not the whole cell) so a transform can never make the
  // flex cell overlap its neighbours OR poke above the row and jag the top edge
  // (transforms don't reflow). The beating cell stands out via its gold border +
  // fill + deeper shadow (styles.cellBeating), NOT a transform on the cell box.
  const scale = pop.interpolate({inputRange: [0, 1], outputRange: [1, 1.22]});

  return (
    <View style={[styles.cell, beating && styles.cellBeating]}>
      <View style={styles.cellHead}>
        <MaterialCommunityIcons
          name={spec.icon}
          size={16}
          color={beating ? GOLD : GREY_TEXT}
        />
        <CCText style={styles.cellLabel}>{spec.label}</CCText>
      </View>
      <Animated.View style={{transform: [{scale}]}}>
        <CCText style={[styles.cellCurrent, beating && styles.cellCurrentBeating]}>
          {current}
        </CCText>
      </Animated.View>
      <CCText style={styles.cellBest}>
        {hasPb || beating ? `Best ${displayBest}` : 'Best —'}
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  cell: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CARD_BG,
    alignItems: 'center',
    // Lift the cards off the cream page (BG_PAGE) so they don't blend on the
    // warm board theme.
    shadowColor: '#4D3F37',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 2},
  },
  cellBeating: {
    borderColor: 'rgba(240, 180, 41, 0.55)',
    backgroundColor: 'rgba(240, 180, 41, 0.08)',
    // A deeper shadow so the lifted (beating) cell visibly stands proud of the
    // other two (item 1).
    shadowColor: '#B8860B',
    shadowOpacity: 0.28,
    shadowRadius: 14,
    shadowOffset: {width: 0, height: 6},
  },
  cellHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 2,
  },
  cellLabel: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '700',
    color: GREY_TEXT,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cellCurrent: {
    fontSize: FontSizes[22] || 22,
    fontWeight: '800',
    color: BROWN,
    lineHeight: 26,
  },
  cellCurrentBeating: {
    color: TEAL,
  },
  cellBest: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '600',
    color: GREY_TEXT,
    marginTop: 1,
  },
});
